import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../components/ui/DataTable/DataTable';
import TableToolbar from '../../../components/ui/DataTable/TableToolbar';
import TablePagination from '../../../components/ui/DataTable/TablePagination';
import FilterDrawer from '../../../components/ui/DataTable/FilterDrawer';
import RowActionsMenu from '../../../components/ui/DataTable/RowActionsMenu';
import StatusPill from '../../../components/ui/StatusPill/StatusPill';
import { Button } from '../../../components/ui/Button2';
import { Icon } from '../../../components/ui/Icon';
import dtStyles from '../../../components/ui/DataTable/DataTable.module.css';

/* Project status configuration — drives the StatusPill in the table
   AND the FilterDrawer options below. tone maps to the shared
   StatusPill primitive's preset palette (see components/ui/StatusPill). */
const STATUS_CONFIG = {
  COMPLETED:   { tone: 'success', label: 'Completed' },
  IN_PROGRESS: { tone: 'warning', label: 'In Progress' },
  NEW:         { tone: 'info',    label: 'New' },
  FAILED:      { tone: 'error',   label: 'Failed' },
};

const STATUS_FILTER_ORDER = ['NEW', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : null);

const ProjectsTable = ({ projects, onEdit, onDelete, loading, onCreateClick }) => {
  const navigate = useNavigate();
  const [sort, setSort] = useState({ column: 'created_at', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusValues, setStatusValues] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Reset to page 1 whenever filters or page-size change so the user
  // never lands on a now-out-of-range page.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusValues, itemsPerPage]);

  /** Live counts per status across the unfiltered project list — shown
   *  next to each option in the FilterDrawer. */
  const statusCounts = useMemo(() => {
    const counts = { NEW: 0, IN_PROGRESS: 0, COMPLETED: 0, FAILED: 0 };
    projects.forEach((p) => {
      if (counts[p.session_status] != null) counts[p.session_status] += 1;
    });
    return counts;
  }, [projects]);

  /** Apply search + status filters → then sort. Both filters are
   *  client-side; the project list is small enough to make this trivial.
   *  No current column declares a custom `sortFn` (plain lexicographic
   *  works for project names / company names; created_at is parsed to
   *  Date explicitly). When a future column needs natural-order sort
   *  (e.g. ThreatTraceabilityPage clause IDs), follow the Themis
   *  pattern: wrap `columns` in useMemo so it's defined before this
   *  useMemo, then dispatch on `column.sortFn`. The `compareNaturally`
   *  utility lives in frontend/src/utils/naturalSort.js. */
  const visibleProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = projects.filter((p) => {
      const matchesSearch =
        q === '' ||
        p.analysis_name?.toLowerCase().includes(q) ||
        p.company_name?.toLowerCase().includes(q);
      const matchesStatus =
        statusValues.length === 0 || statusValues.includes(p.session_status);
      return matchesSearch && matchesStatus;
    });

    if (!sort) return filtered;
    const { column, direction } = sort;
    return [...filtered].sort((a, b) => {
      let av = a[column];
      let bv = b[column];
      if (column === 'created_at') {
        av = new Date(av);
        bv = new Date(bv);
      }
      if (av < bv) return direction === 'asc' ? -1 : 1;
      if (av > bv) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [projects, sort, searchQuery, statusValues]);

  /** Page slice — what actually renders in the table body. */
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return visibleProjects.slice(start, start + itemsPerPage);
  }, [visibleProjects, currentPage, itemsPerPage]);

  /* Column widths are percentages summing to 100% — same pattern as
     every Themis DataTable consumer (the resize logic in DataTable.jsx
     line ~156 explicitly assumes percentage seeds; pixel widths break
     the splitter when freed space gets redistributed across columns). */
  const columns = [
    {
      key: 'analysis_name',
      label: 'Project Name',
      sortable: true,
      width: '16%',
      render: (row) => row.analysis_name || 'Untitled Project',
    },
    {
      key: 'company_name',
      label: 'Company',
      sortable: true,
      width: '12%',
      render: (row) => row.company_name || '-',
    },
    {
      key: 'company_type',
      label: 'Type',
      width: '9%',
      render: (row) =>
        row.company_type === 'Other' && row.company_type_other
          ? row.company_type_other
          : row.company_type || '-',
    },
    {
      key: 'session_status',
      label: 'Status',
      sortable: true,
      width: '9%',
      render: (row) => {
        const cfg = STATUS_CONFIG[row.session_status] ?? STATUS_CONFIG.NEW;
        return <StatusPill tone={cfg.tone} label={cfg.label} size="sm" />;
      },
    },
    {
      key: 'tara_level',
      label: 'Level',
      width: '8%',
      render: (row) => capitalize(row.tara_level) || '-',
    },
    {
      key: 'attack_feasibility_approach',
      label: 'Approach',
      width: '11%',
      render: (row) => row.attack_feasibility_approach || 'Attack Potential',
    },
    {
      key: 'total_assets',
      label: 'Assets',
      align: 'center',
      width: '6%',
      render: (row) => row.total_assets || 0,
    },
    {
      key: 'total_threats',
      label: 'Threats',
      align: 'center',
      width: '6%',
      render: (row) => row.total_threats || 0,
    },
    {
      key: 'critical_risk',
      label: 'Critical',
      align: 'center',
      width: '6%',
      /* Critical number stays the SAME red in both modes (shared
         severity treatment) — uses the mode-invariant --color-error-rgb
         triplet declared in :root. var(--color-status-error) would
         flip between #EF4444 dark and #DC2626 light, which the design
         calls out as wrong here. */
      render: (row) => (
        <span style={{ color: 'rgb(var(--color-error-rgb))', fontWeight: 600 }}>
          {row.critical_risk || 0}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: '11%',
      render: (row) => formatDate(row.created_at),
    },
    {
      /* Athena convention going forward: action columns use a single
         kebab dropdown (RowActionsMenu), no header label. Themis
         convention: 6% width — narrow slot for the ⋮ trigger. */
      key: '_actions',
      label: '',
      align: 'right',
      width: '6%',
      render: (row) => (
        <RowActionsMenu
          ariaLabel={`Actions for ${row.analysis_name || 'project'}`}
          actions={[
            {
              id: 'open',
              label: 'Open Project',
              icon: (
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16" aria-hidden="true">
                  <path
                    d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2V8H20M16 13H8M16 17H8M10 9H8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ),
              onClick: () => navigate(`/project/${row.session_id}`),
            },
            {
              id: 'edit',
              label: 'Edit Project',
              icon: (
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16" aria-hidden="true">
                  <path
                    d="M11 4H4C2.9 4 2 4.9 2 6V20C2 21.1 2.9 22 4 22H18C19.1 22 20 21.1 20 20V13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.5 2.5C19.3 1.7 20.7 1.7 21.5 2.5C22.3 3.3 22.3 4.7 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ),
              onClick: () => onEdit(row.session_id),
            },
            {
              id: 'delete',
              label: 'Delete Project',
              danger: true,
              icon: (
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16" aria-hidden="true">
                  <path
                    d="M3 6H5H21M8 6V4C8 3.5 8.5 3 9 3H15C15.5 3 16 3.5 16 4V6M19 6V20C19 21 18 22 17 22H7C6 22 5 21 5 20V6H19Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ),
              // The page owns the single delete confirmation (AlertDialog);
              // the row action just requests it.
              onClick: () => onDelete(row.session_id),
            },
          ]}
        />
      ),
    },
  ];

  const statusFilter = {
    key: 'status',
    label: 'Status',
    values: statusValues,
    onChange: setStatusValues,
    options: STATUS_FILTER_ORDER.map((s) => ({
      value: s,
      label: STATUS_CONFIG[s].label,
      count: statusCounts[s] ?? 0,
    })),
  };

  const toolbar = (
    <TableToolbar
      search={{
        value: searchQuery,
        onChange: setSearchQuery,
        placeholder: 'Search projects by name or company...',
      }}
      onFilterToggle={() => setFilterOpen((o) => !o)}
      filterButtonActive={filterOpen || statusValues.length > 0}
      actions={
        onCreateClick && (
          <Button
            variant="default"
            onClick={onCreateClick}
            startIcon={<Icon name="add" size={14} />}
          >
            Create New Project
          </Button>
        )
      }
    />
  );

  return (
    <div className={dtStyles.shell}>
      <FilterDrawer
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[statusFilter]}
      />
      <DataTable
        data={paginatedProjects}
        columns={columns}
        rowKey="session_id"
        sort={sort}
        onSortChange={(column, direction) => setSort(column ? { column, direction } : null)}
        onRowClick={(row) => navigate(`/project/${row.session_id}`)}
        loading={loading}
        toolbar={toolbar}
        pagination={
          <TablePagination
            currentPage={currentPage}
            totalItems={visibleProjects.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        }
        emptyState={{
          icon: (
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M3 3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M2 9H22M7 14H12M7 17H10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          ),
          title: 'No Projects Yet',
          description:
            statusValues.length > 0 || searchQuery
              ? 'No projects match the current search or status filters.'
              : 'Create your first project to get started with TARA analysis.',
        }}
      />
    </div>
  );
};

export default ProjectsTable;
